import Vue, { VNode } from 'vue';
import hash from './hash';
import ComponentHelpers from './componentHelpers';
import extend from './extend';
import isPlainObject from './isPlainObject';
import isObject from './isObject';
import isArray from './isArray';
import kindOf from './kindOf';
import getTypeName from './getTypeName';
import deepClone from './deepClone';
import casing from './casing';

class Utils {
  component: ComponentHelpers = new ComponentHelpers();
  /** More detailed ```typeof``` alternative */
  kindOf = kindOf;
  casing = casing;
  /**
   * Creates a deep clone of an object - recursively clones properties of an object.
   * References are **not** preserved.
   */
  deepClone = deepClone;
  hash = hash;
  /**
   * Extends one object with the properties of another.
   * Does **not** deep clone, references **are** preserved.
   */
  extend = extend;
  /** Checks variable to see if it is a plain old object, classes and such are not considered plain objects */
  isPlainObject = isPlainObject;
  /** Checks if variable is an object, does not consider ```null``` and ```Array``` as an object */
  isObject = isObject;
  /** Checks if variable is an array, more extensive than ```Array.isArray``` */
  isArray = isArray;
  getTypeName = getTypeName;

  /**
   * Parses a string and casts it to the most likely type
   * 'true' = boolean
   * 'false' = boolean
   * 'undefined' = undefined
   * 'null' = null
   * '1' = number
   * '1.2' = number
   * 'a string' = string
   */
  parseString(value: string): boolean | number | string | undefined | null {
    if (typeof value !== 'string') {
      return value;
    }
    const lowerCaseValue = value.toLowerCase();
    if (lowerCaseValue === 'false') {
      return false;
    } if (lowerCaseValue === 'true') {
      return true;
    } if (lowerCaseValue === 'undefined') {
      return undefined;
    } if (lowerCaseValue === 'null') {
      return null;
    } if (/^\d+\.\d+$/gm.test(value)) {
      return Number.parseFloat(value);
    } if (/^\d+$/g.test(value)) {
      return Number.parseInt(value, 10);
    }
    return value;
  }

  /**
   * Non-destructive sort, does **not** mutate the passed array
   * @returns A sorted clone of the passed array
   */
  sort<T>(array: T[], sortFunc?: (a: any, b: any) => number): T[] {
    const clonedArray = this.deepClone(array);
    return clonedArray.sort(sortFunc);
  }

  /** Flattens all properties in an object, property paths are sepearted by the delimiter */
  // eslint-disable-next-line default-param-last
  flattenObject(obj: Record<any, any>, delimiter: string = '.', depth?: number) {
    if (depth !== undefined) {
      depth -= 1;
    }
    const toReturn = {} as Record<string, any>;
    Object.keys(obj).forEach((propName) => {
      const propValue = obj[propName];
      if (this.isObject(propValue) || this.isArray(propValue)) {
        if (depth !== undefined && depth <= 0) {
          return;
        }
        const flatObject = this.flattenObject(propValue, delimiter, depth);
        Object.keys(flatObject).forEach((nestedPropName) => {
          toReturn[`${propName}${delimiter}${nestedPropName}`] = flatObject[nestedPropName];
        });
      } else {
        toReturn[propName] = propValue;
      }
    });
    return toReturn;
  }

  /** Retrieves all data attributes from an element in the DOM */
  getAllDataAttributes(node: Node | Vue) {
    type HTMLAttribute = {name: string, value: string};
    const el = this.getElement(node);
    const scriptAttributes = (el?.attributes) ?? [];
    const dataAttributesArray: HTMLAttribute[] = [].filter.call(scriptAttributes, (at: {name: string}) => { return /^data-/.test(at.name); });
    const dataAttributes: Record<string, boolean | string | number | undefined> = {};
    dataAttributesArray.forEach((attr) => {
      const attributeValue = this.parseString(attr?.value) ?? undefined;
      dataAttributes[attr.name.replace('data-', '')] = attributeValue;
    });
    return dataAttributes;
  }

  /** Gets the element, useful when getting element from component reference */
  getElement(node: Node | Vue | VNode): Element {
    return ((node as VNode)?.elm as Element) ?? (node as Vue)?.$el ?? node;
  }

  /**
   * Makes a selection based on the element passed and copies that to the clipboard
   * @param node The node to copy the contents from, can also be an instance of a Vue component
   */
  async copyToClipboard(node: Node | Vue) {
    const element = this.getElement(node);
    const textValue = this.selectText(element);
    if (textValue) {
      if (typeof navigator?.clipboard?.writeText === 'function') {
        await navigator.clipboard.writeText(textValue);
        return textValue;
      }
      try {
        // For older browsers
        document.execCommand('copy');
      } catch (error) {
        Vue.$log.error(`Unable to copy text to clipboard: ${error}`);
        return undefined;
      }
      return textValue;
    }
    Vue.$log.warning('Unable to select any text to write to clipboard');
    return undefined;
  }

  selectText(node: Node | Vue): string | undefined {
    const el = this.getElement(node) as HTMLInputElement;
    if (typeof el.select === 'function') {
      // in case of input fields
      el.select();
      return el.value;
    }
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(el);
      selection.removeAllRanges();
      selection.addRange(range);
      const text = selection.getRangeAt(0).toString();
      return text;
    }
    Vue.$log.error('Unable to select text, unable to retrieve Selection object');
    return undefined;
  }

  /**
   * @example ```_.nameof(() => window.aProp.nested) === 'aProp.nested'```
   * @returns Returns prop path as string
   */
  nameof(exp: () => any) {
    const propPathMatches = exp?.toString().match(/([.](\w+))+/g)!;
    if (propPathMatches && propPathMatches?.length > 0) {
      return propPathMatches[0].substring(1);
    }
    throw new TypeError(`Unable to get nameof from ${exp?.toString()}`);
  }

  isPromise<T = any>(val: any): val is Promise<T> {
    return Boolean(val) && getTypeName(val) === 'Promise' && typeof (val as any)?.then === 'function';
  }

  /**
   * Get a nested property value
   * @param obj object on which to get nested property value
   * @param key name/path of property delimited by .
   */
  deepGet<T = any>(obj: any, key: string | string[]): T | undefined {
    if (!isObject(obj)) {
      return undefined;
    }
    if (!(typeof key === 'string' || isArray(key))) {
      return undefined;
    }
    const properties: Array<string> = this.isArray(key) ? key : key.split('.');
    return properties.reduce((prev: any, curr: string) => prev && prev[curr], obj);
  }

  /**
   * Sets a nested property value
   * @param obj object on which property is set
   * @param key name/path of property delimited by the provided delimiter
   */
  deepSet<T = any>(obj: any, key: string | string[], value: T, delimiter: string = '.'): void {
    if (!isObject(obj)) {
      return;
    }
    if (!(typeof key === 'string' || this.isArray(key))) {
      return;
    }
    const properties: Array<string> = this.isArray(key) ? key : key.split(delimiter);
    if (properties.length === 0) {
      return;
    }
    const propName = properties.pop();
    const nestedObj = properties.reduce((currObj: any, currPropName: any) => {
      const notSettable = currObj[currPropName] && !(this.isObject(currObj[currPropName]) || this.isArray(currObj[currPropName]));
      if (notSettable) {
        return undefined;
      }
      currObj[currPropName] = currObj[currPropName] ?? {};
      return currObj[currPropName];
    }, obj);
    if (nestedObj) {
      Vue.set(nestedObj, propName!, value);
    }
  }

  /**
   * Checks if value is an empty string, array, or object (null and undefined are considered empty).
   * This is not a [falsy](https://developer.mozilla.org/en-US/docs/Glossary/Falsy) check so 0 is not empty for instance
   */
  isEmpty(value: any): boolean {
    return value === null
        || value === undefined
        || (Object.entries(value).length === 0 && value.constructor === Object)
        || value === ''
        || (Array.isArray(value) && value.length === 0);
  }
  /**
   * Cheks if a value is ```null``` or ```undefined```
   */
  isNil(value: any): value is null | undefined {
    return value === undefined || value === null;
  }
  /**
   * Checks if a value is **not** ```null``` and **not** ```undefined```
   */
  isSet<T>(value: T): value is NonNullable<T> {
    return value !== undefined && value !== null;
  }

  /**
   * @deprecated Use the null coalescing operator instead -> ??
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Nullish_coalescing_operator
   */
  defaultTo<V, A>(value: V, alternative: A): V | A {
    if (this.isNil(value)) {
      return alternative;
    }
    return value;
  }
}

declare module 'vue/types/vue' {
  // eslint-disable-next-line no-shadow
  interface Vue {
    _: Utils
  }
  interface VueConstructor {
    _: Utils
  }
}

const _ = new Utils();
Vue._ = _;
Vue.prototype._ = _;
export default _;
